// Routes/Views del módulo Documentos (Drive multi-tenant).
// El registry declara los tabs:
//   /m/documents/mine          → Mis documentos
//   /m/documents/shared        → Compartidos conmigo
//   /m/documents/org           → De la organización
//   /m/documents/upload        → Subir nuevo

import { useState } from 'react';
import { FileText, Upload, X } from 'lucide-react';

import { OsApp } from '@/modules/orion-shell/OsApp';
import { DocumentList } from './components/DocumentList';
import { DocumentUpload } from './components/DocumentUpload';
import { ShareDialog } from './components/ShareDialog';
import type { DocumentOut, Tab } from './types';

const TITLES: Record<Tab, { title: string; subtitle: string }> = {
  mine: { title: 'Mis documentos', subtitle: 'Documentos que tú has subido — personales o compartidos.' },
  shared_with_me: { title: 'Compartidos conmigo', subtitle: 'Otros usuarios de tu organización compartieron contigo estos archivos.' },
  org: { title: 'De la organización', subtitle: 'Documentos visibles para toda la organización.' },
  all: { title: 'Todos los documentos', subtitle: 'Todo lo que puedes ver en tu organización.' },
};

// Subir vive EN la lista (2026-11-04): antes solo estaba en la pestaña «Subir nuevo»
// del menú lateral, que en celular/tablet queda escondida — nadie encontraba cómo subir.
function DocumentsListView({ tab }: { tab: Tab }) {
  const [refreshKey, setRefreshKey] = useState(0);
  const [sharing, setSharing] = useState<DocumentOut | null>(null);
  const [uploading, setUploading] = useState(false);
  const t = TITLES[tab];

  return (
    <div className="mx-auto w-full max-w-6xl px-3 py-4 sm:px-6 sm:py-6 lg:px-8">
      <OsApp
        icon={FileText}
        title={t.title}
        subtitle={t.subtitle}
        actions={
          <button
            type="button"
            onClick={() => setUploading((v) => !v)}
            aria-expanded={uploading}
            className={
              uploading
                ? 'inline-flex h-9 items-center gap-1.5 rounded-xl px-3 text-sm font-medium text-foreground hover:bg-muted'
                : 'os-btn-brand inline-flex h-9 items-center gap-1.5 rounded-xl px-3.5 text-sm font-semibold'
            }
          >
            {uploading ? <X size={16} aria-hidden /> : <Upload size={16} aria-hidden />}
            {uploading ? 'Cerrar' : 'Subir documentos'}
          </button>
        }
      >
        {uploading && (
          <div className="mb-4">
            <DocumentUpload onUploaded={() => setRefreshKey((k) => k + 1)} />
          </div>
        )}
        <DocumentList
          tab={tab}
          refreshKey={refreshKey}
          onShareClick={(doc) => setSharing(doc)}
        />
      </OsApp>
      {sharing && (
        <ShareDialog
          doc={sharing}
          onClose={() => setSharing(null)}
          onShared={() => setRefreshKey((k) => k + 1)}
        />
      )}
    </div>
  );
}

export function DocumentsMineView() {
  return <DocumentsListView tab="mine" />;
}

export function DocumentsSharedView() {
  return <DocumentsListView tab="shared_with_me" />;
}

export function DocumentsOrgView() {
  return <DocumentsListView tab="org" />;
}

export function DocumentsUploadView() {
  // No refresh callback aquí — el user verá el doc nuevo al cambiar de tab.
  return (
    <div className="mx-auto w-full max-w-6xl px-3 py-4 sm:px-6 sm:py-6 lg:px-8">
      <OsApp icon={Upload} title="Subir documentos" subtitle="Arrastra y suelta. Se sube apenas lo dejes — sin formulario, sin esperas.">
        <DocumentUpload />
      </OsApp>
    </div>
  );
}
